import { Container, Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';

const MAP_WIDTH = 100;
const MAP_HEIGHT = 70;
const MARGIN = 4;

// Room type colors
const ROOM_COLORS = {
  entrance: 0x4488ff,
  standard: 0x666666,
  treasure: 0xffcc44,
  shop: 0x44cc66,
  boss: 0xcc3333,
};

const ROOM_COLORS_CLEARED = {
  entrance: 0x6699ff,
  standard: 0x999999,
  treasure: 0xffdd77,
  shop: 0x66dd88,
  boss: 0xff5555,
};

/**
 * PixiJS-rendered minimap overlay (top-right corner).
 * Shows rooms as colored rectangles, player dot, and fog of war.
 */
export class Minimap {
  /**
   * @param {Container} uiContainer - The HUD / UI container (fixed, not world)
   * @param {number} screenWidth - Game screen width
   */
  constructor(uiContainer, screenWidth) {
    this.uiContainer = uiContainer;
    this.screenWidth = screenWidth;
    this.visible = true;

    this.rooms = [];
    this.dungeonWidth = 60;
    this.dungeonHeight = 40;
    this.visitedRooms = new Set();
    this.clearedRooms = new Set();
    this.currentRoomIndex = -1;

    // Player position in tiles
    this.playerTileX = 0;
    this.playerTileY = 0;

    // Container for the minimap
    this.container = new Container();
    this.container.x = screenWidth - MAP_WIDTH - MARGIN;
    this.container.y = MARGIN;
    this.uiContainer.addChild(this.container);

    // Background
    this.bg = new Graphics();
    this.container.addChild(this.bg);

    // Rooms layer
    this.roomsGfx = new Graphics();
    this.container.addChild(this.roomsGfx);

    // Player dot
    this.playerDot = new Graphics();
    this.container.addChild(this.playerDot);
  }

  /**
   * Set dungeon data for minimap rendering.
   * @param {object[]} rooms - Array of room objects from DungeonGenerator
   * @param {number} dungeonWidth - Dungeon width in tiles
   * @param {number} dungeonHeight - Dungeon height in tiles
   */
  setDungeon(rooms, dungeonWidth, dungeonHeight) {
    this.rooms = rooms;
    this.dungeonWidth = dungeonWidth;
    this.dungeonHeight = dungeonHeight;
    this.visitedRooms.clear();
    this.clearedRooms.clear();
    this.currentRoomIndex = -1;

    // Always show entrance room
    const entranceIdx = rooms.findIndex(r => r.type === 'entrance');
    if (entranceIdx >= 0) {
      this.visitedRooms.add(entranceIdx);
    }

    this._redraw();
  }

  /**
   * Mark a room as visited (revealed on minimap).
   * @param {number} roomIndex - Index in rooms array
   */
  visitRoom(roomIndex) {
    if (roomIndex >= 0) {
      this.visitedRooms.add(roomIndex);
    }
  }

  /**
   * Mark a room as cleared.
   * @param {number} roomIndex - Index in rooms array
   */
  clearRoom(roomIndex) {
    if (roomIndex >= 0) {
      this.clearedRooms.add(roomIndex);
    }
  }

  /**
   * Update the minimap display.
   * @param {number} playerX - Player pixel X
   * @param {number} playerY - Player pixel Y
   * @param {number} currentRoomIndex - Index of room player is in (-1 if in corridor)
   */
  update(playerX, playerY, currentRoomIndex) {
    if (!this.visible) {
      this.container.visible = false;
      return;
    }
    this.container.visible = true;

    // Visit the current room
    if (currentRoomIndex >= 0) {
      this.visitedRooms.add(currentRoomIndex);
    }

    this.playerTileX = playerX / TILE_SIZE;
    this.playerTileY = playerY / TILE_SIZE;
    this.currentRoomIndex = currentRoomIndex;

    this._redraw();
  }

  /**
   * Toggle minimap visibility.
   */
  toggle() {
    this.visible = !this.visible;
    this.container.visible = this.visible;
  }

  /**
   * Redraw all minimap graphics.
   */
  _redraw() {
    // Scale factors: map tile coords to minimap pixel coords
    const sx = MAP_WIDTH / this.dungeonWidth;
    const sy = MAP_HEIGHT / this.dungeonHeight;

    // Background
    this.bg.clear();
    this.bg.rect(0, 0, MAP_WIDTH, MAP_HEIGHT);
    this.bg.fill({ color: 0x000000, alpha: 0.6 });
    this.bg.rect(0, 0, MAP_WIDTH, MAP_HEIGHT);
    this.bg.stroke({ width: 1, color: 0x333344 });

    // Rooms
    this.roomsGfx.clear();
    for (let i = 0; i < this.rooms.length; i++) {
      if (!this.visitedRooms.has(i)) continue; // fog of war

      const room = this.rooms[i];
      const rx = Math.round(room.x * sx);
      const ry = Math.round(room.y * sy);
      const rw = Math.max(2, Math.round(room.width * sx));
      const rh = Math.max(2, Math.round(room.height * sy));

      const cleared = this.clearedRooms.has(i);
      const colorMap = cleared ? ROOM_COLORS_CLEARED : ROOM_COLORS;
      const color = colorMap[room.type] || colorMap.standard;

      this.roomsGfx.rect(rx, ry, rw, rh);
      this.roomsGfx.fill(color);

      // Highlight current room with white border
      if (i === this.currentRoomIndex) {
        this.roomsGfx.rect(rx, ry, rw, rh);
        this.roomsGfx.stroke({ width: 1, color: 0xffffff });
      }
    }

    // Player dot
    this.playerDot.clear();
    const px = Math.round(this.playerTileX * sx);
    const py = Math.round(this.playerTileY * sy);
    this.playerDot.circle(px, py, 2);
    this.playerDot.fill(0xffffff);
  }

  /**
   * Clean up.
   */
  destroy() {
    if (this.container && this.container.parent) {
      this.container.parent.removeChild(this.container);
    }
    this.container.destroy({ children: true });
  }
}
